import { useState, useEffect } from 'react'
import { motion, AnimatePresence, useMotionValue, useSpring, useReducedMotion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import Marquee from 'react-fast-marquee'

const WORDS = ['Foundation', 'Leadership', 'Teams', 'Future']

const logos = [
  { name: 'Wesfarmers', url: '/logos/wesfarmers.png' },
  { name: 'Aditya Birla Group', url: '/logos/adityabirla.png' },
  { name: 'Kmart', url: '/logos/kmart.svg' },
  { name: 'BigBasket', url: '/logos/bigbasket.png' },
  { name: 'Anko', url: '/logos/anko.png' },
  { name: 'Dream11', url: '/logos/dream11.png' },
  { name: 'Wakefit', url: '/logos/wakefit.webp' },
  { name: 'Truemeds', url: '/logos/truemeds.jpg' },
  { name: 'TIFIN', url: '/logos/tifin.webp' },
  { name: 'Betterplace', url: '/logos/betterplace.png' },
  { name: 'Minus Zero', url: '/logos/minuszero.webp' },
  { name: 'Niqo Robotics', url: '/logos/niqo.webp' },
  { name: 'Hectar', url: '/logos/hectar.webp' },
  { name: 'Smile Group', url: '/logos/smile.png' },
  { name: 'Enboarder', url: '/logos/enboarder.png' },
  { name: 'Wiser', url: '/logos/wiser.png' },
  { name: 'Nayan', url: '/logos/nayan.png' },
  { name: 'Neutrinos', url: '/logos/neutrinos.jpg' },
  { name: 'Altcase', url: '/logos/altcase.jpg' },
  { name: 'EdiQue', url: '/logos/edique.webp' },
  { name: 'HiAstro', url: '/logos/hiastro.png' },
]

function MagneticCTA({ children, to, className, testId }) {
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const sx = useSpring(x, { stiffness: 250, damping: 20 })
  const sy = useSpring(y, { stiffness: 250, damping: 20 })

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect()
    x.set((e.clientX - (r.left + r.width / 2)) * 0.3)
    y.set((e.clientY - (r.top + r.height / 2)) * 0.3)
  }

  return (
    <motion.div
      style={{ x: sx, y: sy, display: 'inline-block' }}
      onMouseMove={onMove}
      onMouseLeave={() => { x.set(0); y.set(0) }}
    >
      <Link to={to} className={className} data-testid={testId}>{children}</Link>
    </motion.div>
  )
}

const lineV = {
  hidden: { y: '115%' },
  visible: (i) => ({
    y: '0%',
    transition: { duration: 1.05, ease: [0.16, 1, 0.3, 1], delay: 0.05 + i * 0.11 },
  }),
}

export default function HeroSection() {
  const [wordIdx, setWordIdx] = useState(0)
  const prefersReducedMotion = useReducedMotion()

  useEffect(() => {
    const t = setInterval(() => setWordIdx(i => (i + 1) % WORDS.length), 3000)
    return () => clearInterval(t)
  }, [])

  return (
    <section
      data-testid="hero-section"
      className="min-h-screen bg-white flex flex-col pt-20 relative overflow-hidden"
    >
      {/* Background texture line */}
      <div className="absolute right-0 top-0 bottom-0 w-px bg-[#E5E7EB] hidden lg:block" />

      {/* Main content — flex-1 so it fills available height, vertically centered */}
      <motion.div
        initial="hidden"
        animate="visible"
        className="flex-1 flex flex-col justify-center px-6 md:px-12 lg:px-24 py-12 relative z-10"
      >
        <div className="max-w-7xl">
          {/* Headline */}
          <h1 className="mb-10">
            <div className="overflow-hidden">
              <motion.div
                custom={1} variants={lineV}
                className="text-[clamp(2.25rem,5.6vw,6rem)] font-heading font-black tracking-[-0.03em] leading-[0.92] text-[#0A0A0A]"
              >
                We build the
              </motion.div>
            </div>

            {/* Rotating word row */}
            <div className="overflow-hidden">
              <motion.div custom={2} variants={lineV}>
                <div
                  className="relative overflow-hidden text-[clamp(2.25rem,5.6vw,6rem)] font-heading font-black tracking-[-0.03em] leading-[0.92]"
                  style={{ height: 'clamp(2.25rem,5.6vw,6rem)' }}
                >
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                      key={WORDS[wordIdx]}
                      initial={{ y: '105%' }}
                      animate={{ y: '0%' }}
                      exit={{ y: '-105%' }}
                      transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute inset-0 text-[#0B1F3A]"
                      data-testid="hero-rotating-word"
                    >
                      {WORDS[wordIdx]}
                    </motion.span>
                  </AnimatePresence>
                </div>
              </motion.div>
            </div>

            <div className="overflow-hidden">
              <motion.div
                custom={3} variants={lineV}
                className="text-[clamp(2.25rem,5.6vw,6rem)] font-heading font-light tracking-[-0.03em] leading-[0.92] text-[#16365F]"
              >
                of top Tech Firms &amp; GCCs
              </motion.div>
            </div>
          </h1>

          {/* CTAs */}
          <div className="overflow-hidden">
            <motion.div custom={4} variants={lineV} className="flex items-center gap-8 flex-wrap">
              <MagneticCTA
                to="/contact"
                testId="hero-cta-primary"
                className="inline-flex items-center gap-3 bg-[#0A0A0A] text-white text-[10px] font-body font-semibold tracking-[0.18em] uppercase px-8 py-4 rounded-none hover:bg-[#0B1F3A] transition-colors duration-300"
              >
                Start Your Search <ArrowRight size={13} />
              </MagneticCTA>
              <Link
                to="/insights"
                data-testid="hero-cta-secondary"
                className="text-[10px] font-body font-semibold tracking-[0.18em] uppercase text-[#0A0A0A] border-b border-[#0A0A0A]/40 pb-0.5 hover:text-[#16365F] hover:border-[#16365F] transition-colors duration-200"
              >
                Read Our Insights
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Logo strip — always visible at the bottom of the hero viewport */}
      <motion.div
        data-testid="hero-logo-strip"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.9 }}
        className="relative z-10 border-t border-[#E5E7EB]"
      >
        <p className="text-[9px] tracking-[0.32em] uppercase text-[#0A0A0A]/25 font-body px-6 md:px-12 lg:px-24 pt-5 pb-5">
          Trusted by technology companies, GCCs and high-growth businesses
        </p>

        {prefersReducedMotion ? (
          /* Static grid for prefers-reduced-motion */
          <div className="pb-8 px-6 md:px-12 lg:px-24 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-8 gap-y-6">
            {logos.map(logo => (
              <div
                key={logo.name}
                className="flex items-center justify-center"
                style={{ height: 64 }}
              >
                <img
                  src={logo.url}
                  alt={logo.name}
                  style={{
                    maxHeight: 42,
                    maxWidth: 140,
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    objectPosition: 'center',
                    transform: logo.scale ? `scale(${logo.scale})` : undefined,
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          /* Seamless infinite marquee */
          <div className="relative overflow-hidden pb-7">
            {/* Fade edge — left */}
            <div
              className="absolute left-0 top-0 bottom-7 w-24 z-10 pointer-events-none"
              style={{ background: 'linear-gradient(to right, #ffffff 40%, transparent)' }}
            />
            {/* Fade edge — right */}
            <div
              className="absolute right-0 top-0 bottom-7 w-24 z-10 pointer-events-none"
              style={{ background: 'linear-gradient(to left, #ffffff 40%, transparent)' }}
            />
            <Marquee speed={32} pauseOnHover gradient={false} autoFill>
              {logos.map(logo => (
                <div
                  key={logo.name}
                  className="flex items-center justify-center"
                  style={{
                    height: 80,
                    width: 200,
                    marginLeft: 32,
                    marginRight: 32,
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={logo.url}
                    alt={logo.name}
                    style={{
                      height: 52,
                      width: 'auto',
                      maxWidth: 168,
                      objectFit: 'contain',
                      objectPosition: 'center',
                    }}
                  />
                </div>
              ))}
            </Marquee>
          </div>
        )}
      </motion.div>
    </section>
  )
}
